import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, Text, TextField, Select, Spinner, Heading } from '@radix-ui/themes';
import { ChevronLeftIcon } from '@radix-ui/react-icons';
import * as Yup from 'yup';
import { updateUser } from '../../redux/slices/userSlice';
import { updateAccount } from '../../redux/slices/accountSlice';
import { toast } from 'react-toastify';
import OtpInput from 'react-otp-input';
import { updateTeacherAccount } from '../../redux/slices/teacherAccountSlice';
import { updateStudentAccount } from '../../redux/slices/studentAccountSlice';
import { updateGuardian } from '../../redux/slices/guardianSlice';
import { updateSchoolAdminAccount } from '../../redux/slices/schoolAdminAccountSlice';
import api from '../../api';
import SchoolSearch from '../schoolSearch/SchoolSearch';

const RegisterForm = ({ setRegisterRole }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [OTPLoading, setOTPLoading] = useState(false)
  const [levels, setLevels] = useState([]);
  const [countries, setCountries] = useState([]); // State for countries
  const [regions, setRegions] = useState([]); // State for regions
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [step, setStep] = useState(1);
  const [submittedData, setSubmittedData] = useState(null); //Data on step 1 
  const [verificationRequestData, setVerificationRequestData] = useState(null)
  const [otp, setOtp] = useState("");

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'student',
      schoolName: '',
      schoolLocation: '',
      schoolSector: 'private',
      level: selectedLevel,
      country_id: '',
      region_id: '',
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .matches(/^[0-9]{7,10}$/, 'Phone number must be between 7 and 10 digits')
        .required('Phone number is required'),
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string()
        .email('Invalid email')
        .notRequired(),
      role: Yup.string().required('Role is required'),
      level: Yup.string().when('role', {
        is: (value) => value === 'student',
        then: () => Yup.string().required('Level is required'),
        otherwise: () => Yup.string().notRequired(),
      }),
      schoolName: Yup.string().when('role', {
        is: (value) => value === 'school',
        then: () => Yup.string().required('School name is required'),
        otherwise: () => Yup.string().notRequired(),
      }),
      schoolLocation: Yup.string().when('role', {
        is: (value) => value === 'school',
        then: () => Yup.string().required('School location is required'),
        otherwise: () => Yup.string().notRequired(),
      }),
      country_id: Yup.string().when('role', {
        is: (value) => value === 'school',
        then: () => Yup.string().required('Country is required'),
        otherwise: () => Yup.string().notRequired(),
      }),
      region_id: Yup.string().when('role', {
        is: (value) => value === 'school',
        then: () => Yup.string().required('Region is required'),
        otherwise: () => Yup.string().notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      try {
        formik.setStatus('');
        setLoading(true);
        const userData = {
          firstname: values.firstName,
          lastname: values.lastName,
          email: values.email,
          phone: values.phone,
          password: values.password,
          password_confirmation: values.confirmPassword,
          role: values.role,
          school_name: values.schoolName,
          school_location: values.schoolLocation,
          school_sector: values.schoolSector,
          level: values.level,
          school_id: selectedSchoolId,
          country_id: values.country_id,
          region_id: values.region_id,
        };

        const response = await api.post(`/register`, userData);
        const data = response.data;

        if (data.errors) {
          formik.setErrors(data.errors);
        } else {
          setVerificationRequestData(data.details.data);
          setStep(3);
          setSubmittedData(userData);
        }
      } catch (err) {
        console.log('check errors::', err);
        if (err.response?.data.errors) {
          formik.setErrors(err.response?.data.errors);
        } else if (err.response?.data.message) {
          formik.setStatus(err.response?.data.message);
        } else if (err.response?.data.error) {
          formik.setStatus(err.response?.data.error);
        } else {
          formik.setStatus('Registration failed. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch countries when component mounts
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.get('/countries');
        setCountries(response.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
        toast.error('Failed to load countries', { position: 'bottom-right' });
      }
    };
    fetchCountries();
  }, []);

  // Fetch regions when country_id changes
  useEffect(() => {
    if (formik.values.country_id) {
      const fetchRegions = async () => {
        try {
          const response = await api.get(`/regions/${formik.values.country_id}`);
          setRegions(response.data);
        } catch (error) {
          console.error('Error fetching regions:', error);
          toast.error('Failed to load regions', { position: 'bottom-right' });
        }
      };
      fetchRegions();
    } else {
      setRegions([]); // Clear regions if no country is selected
      formik.setFieldValue('region_id', ''); // Reset region_id
    }
  }, [formik.values.country_id]);

  const handleVerification = async (e) => {
    e.preventDefault();
    setOTPLoading(true);

    if (otp.length < 4) {
      toast.error('Enter the OTP', { position: 'bottom-right' });
      setOTPLoading(false);
      return;
    }

    try {
      const userData = {
        ...submittedData,
        request_id: verificationRequestData.requestId ?? verificationRequestData.id,
        prefix: verificationRequestData.prefix,
        code: otp,
      };

      const response = await api.post(`/verify-register`, userData);
      const data = response.data;
      console.log('begin clearing data')
      
      localStorage.clear();

      dispatch(updateUser({ ...data.user, account: data.account.details }));
      dispatch(updateAccount(data.account));
      localStorage.setItem('authToken', data.user.authToken);
      // localStorage.setItem('userDetails', JSON.stringify(data.user.details));
      // localStorage.setItem('accountDetails', JSON.stringify(data.account));

      switch (data.user.details.role) {
        case 'student':
          console.log('student accountxx::', data.account);
          dispatch(updateStudentAccount(data.account));
          navigate('/student/payments');
          break;
        case 'guardian':
          dispatch(updateGuardian(data.account));
          navigate('/guardian');
          break;
        case 'school':
          dispatch(updateSchoolAdminAccount(data.account));
          navigate('/school');
          break;
        case 'teacher':
          dispatch(updateTeacherAccount(data.account));
          navigate('/teacher/schedule');
          break;
        default:
          navigate('/dashboard');
          break;
      }
    } catch (err) {
      console.log('error verifying::', err);
      toast.error(err.response?.data.error || 'An unexpected error occurred, try again', {
        position: 'bottom-right',
      });
    } finally {
      setOTPLoading(false);
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    try {
      const userData = {
        request_id: verificationRequestData.requestId ?? verificationRequestData.id,
      };
      const response = await api.post(`/resend-otp`, userData);
      setVerificationRequestData(response.data.details.data);
    } catch (err) {
      console.log('error resending otp::', err.response.data.error);
      toast.error(err.response.data.error, { position: 'bottom-right' });
    }
  };

  useEffect(() => {
    if (formik.values.role === 'student') {
      const fetchLevels = async () => {
        try {
          const response = await api.get('/levels');
          setLevels(response.data);
        } catch (error) {
          console.error('Error fetching levels:', error);
        }
      };
      fetchLevels();
    }
  }, [formik.values.role]);

  const handleSelectSchool = (schoolId) => {
    if (selectedSchoolId === schoolId) {
      // Deselect if the same school is clicked
      setSelectedSchoolId(null);
      formik.setFieldValue('teacherSchoolId', '');
    } else {
      // Select the new school
      setSelectedSchoolId(schoolId);
      formik.setFieldValue('teacherSchoolId', schoolId);
    }
  };


  /**
   * Resets the OTP and goes back to step 1
   */
  const handleGoBack = () => {
    setStep(step - 1)
    setOtp("")

    if (step == 1) {
      setRegisterRole('')
    }
  }


  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.status && (
        <Flex direction="column" justify="center" align="center">
          <Text color="red" mb="4">{formik.status}</Text>
        </Flex>
      )}

      {step == 1 && (
        <Flex direction="column" gap="3">
          <Flex direction="column" gap="3" align="center" justify="center">
            <Text size="4" mb="4">Register as?</Text>
          </Flex>

          <Select.Root
            name="role"
            value={formik.values.role}
            onValueChange={(value) => {
              formik.setFieldValue('role', value);
              setRegisterRole(value);
            }}
          >
            <Select.Trigger />
            <Select.Content>
              <Select.Item value="student">Student</Select.Item>
              <Select.Item value="teacher">Teacher</Select.Item>
              <Select.Item value="guardian">Guardian</Select.Item>
              <Select.Item value="school">School Admin</Select.Item>
            </Select.Content>
          </Select.Root>

          <Button disabled={loading} type="submit" className="cursor-pointer" onClick={() => setStep(2)}>
            Continue
          </Button>
        </Flex>
      )}

      {step === 2 && (
        <Flex direction="column" gap="3">
          <p className="text-lg cursor-pointer" onClick={handleGoBack}>
            <span className="font-bold text-red-500">Go back</span>
          </p>

          {/* Phone Number - Required */}
          <Flex direction="column" gap="1">
            <Text size="2">
              Phone Number <Text as="span" color="red">*</Text>
            </Text>
            <TextField.Root
              size="3"
              name="phone"
              type="tel"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter phone number"
            />
            {formik.touched.phone && formik.errors.phone && (
              <Text color="red" size="2">{formik.errors.phone}</Text>
            )}
          </Flex>

          {/* Email - Optional */}
          <Flex direction="column" gap="1">
            <Text size="2">Email Address</Text>
            <TextField.Root
              size="3"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter email (optional)"
            />
            {formik.touched.email && formik.errors.email && (
              <Text color="red" size="2">{formik.errors.email}</Text>
            )}
          </Flex>

          {/* First Name - Required */}
          <Flex direction="column" gap="1">
            <Text size="2">
              First Name <Text as="span" color="red">*</Text>
            </Text>
            <TextField.Root
              size="3"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter first name"
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <Text color="red" size="2">{formik.errors.firstName}</Text>
            )}
          </Flex>

          {/* Last Name - Required */}
          <Flex direction="column" gap="1">
            <Text size="2">
              Last Name <Text as="span" color="red">*</Text>
            </Text>
            <TextField.Root
              size="3"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter last name"
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <Text color="red" size="2">{formik.errors.lastName}</Text>
            )}
          </Flex>

          {/* School Search - Conditional for teacher/student */}
          {(formik.values.role === 'teacher' || formik.values.role === 'student') && (
            <Flex direction="column" gap="1">
              <Text size="2">
                Select School <Text as="span">(Optional)</Text>
              </Text>
              <SchoolSearch
                selectedSchoolId={selectedSchoolId}
                onSelect={handleSelectSchool}
              />
            </Flex>
          )}

          {/* Level Selection - Required for student */}
          {formik.values.role === 'student' && (
            <Flex direction="column" gap="3" wrap="wrap" justify="center">
              <Text size="2">
                Select a Level <Text as="span" color="red">*</Text>
              </Text>
              <Flex direction="row" gap="3" wrap="wrap" justify="center">
                {levels.map((level) => (
                  <Button
                    className="cursor-pointer"
                    key={level.id}
                    variant={selectedLevel === level.id ? 'solid' : 'soft'}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedLevel(level.id);
                      formik.setValues({ ...formik.values, level: level.id });
                    }}
                  >
                    {level.label}
                  </Button>
                ))}
              </Flex>
              {formik.touched.level && formik.errors.level && (
                <Text color="red" size="2">{formik.errors.level}</Text>
              )}
            </Flex>
          )}

          {/* School Admin Fields - Conditional */}
          {formik.values.role === 'school' && (
            <>
              {/* School Name - Required */}
              <Flex direction="column" gap="1">
                <Text size="2">
                  School Name <Text as="span" color="red">*</Text>
                </Text>
                <TextField.Root
                  size="3"
                  name="schoolName"
                  value={formik.values.schoolName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter school name"
                />
                {formik.touched.schoolName && formik.errors.schoolName && (
                  <Text color="red" size="2">{formik.errors.schoolName}</Text>
                )}
              </Flex>

              {/* Country - Required */}
              <Flex direction="column" gap="1">
                <Text size="2">
                  Country <Text as="span" color="red">*</Text>
                </Text>
                <Select.Root
                  name="country_id"
                  value={formik.values.country_id}
                  onValueChange={(value) => formik.setFieldValue('country_id', value)}
                >
                  <Select.Trigger placeholder='Select country'>
                    {formik.values.country_id
                      ? countries.find((country) => country.id == formik.values.country_id)?.name || 'Select country'
                      : 'Select country'}
                  </Select.Trigger>
                  <Select.Content>
                    {/* <Select.Item value=" " disabled>
                      Select country
                    </Select.Item> */}
                    {countries.map((country) => (
                      <Select.Item key={country.id} value={country.id}>
                        {country.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
                {formik.touched.country_id && formik.errors.country_id && (
                  <Text color="red" size="2">{formik.errors.country_id}</Text>
                )}
              </Flex>

              {/* Region - Required */}
              <Flex direction="column" gap="1">
                <Text size="2">
                  Region <Text as="span" color="red">*</Text>
                </Text>
                <Select.Root
                  name="region imprensa_id"
                  value={formik.values.region_id}
                  onValueChange={(value) => formik.setFieldValue('region_id', value)}
                  disabled={!formik.values.country_id} // Disable until country is selected
                >
                  <Select.Trigger placeholder='Select region'>
                    {formik.values.region_id
                      ? regions.find((region) => region.id == formik.values.region_id)?.name || 'Select region'
                      : 'Select region'}
                  </Select.Trigger>
                  <Select.Content>
                    {/* <Select.Item value=" " disabled>
                      Select region
                    </Select.Item> */}
                    {regions.map((region) => (
                      <Select.Item key={region.id} value={region.id}>
                        {region.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
                {formik.touched.region_id && formik.errors.region_id && (
                  <Text color="red" size="2">{formik.errors.region_id}</Text>
                )}
              </Flex>

              {/* School Location - Required */}
              <Flex direction="column" gap="1">
                <Text size="2">
                  School Location <Text as="span" color="red">*</Text>
                </Text>
                <TextField.Root
                  size="3"
                  name="schoolLocation"
                  value={formik.values.schoolLocation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter school location"
                />
                {formik.touched.schoolLocation && formik.errors.schoolLocation && (
                  <Text color="red" size="2">{formik.errors.schoolLocation}</Text>
                )}
              </Flex>


              {/* School Sector - Optional */}
              <Flex direction="column" gap="1">
                <Text size="2">School Sector</Text>
                <Select.Root
                  name="schoolSector"
                  value={formik.values.schoolSector}
                  onValueChange={(value) => formik.setFieldValue("schoolSector", value)}
                >
                  <Select.Trigger />
                  <Select.Content>
                    <Select.Item value="private">Private</Select.Item>
                    <Select.Item value="public">Public</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>
            </>
          )}

          <Button disabled={loading} type="submit" className="cursor-pointer">
            {loading ? (
              <Flex gap="2" align="center">
                <Spinner size="1" />
                Loading...
              </Flex>
            ) : (
              "Register"
            )}
          </Button>

          <Button
            onClick={() => navigate("/login")}
            color="cyan"
            variant="soft"
            className="cursor-pointer"
          >
            Already have an account?
          </Button>
        </Flex>
      )}

      {step === 3 && (
        <Flex direction="column" gap="1" align="center" justify="center">
          <Flex align="center" onClick={handleGoBack} className="cursor-pointer">
            <ChevronLeftIcon />
            <Text color="blue">Go back</Text>
          </Flex>

          <p className="text-lg">
            Enter the verification code sent to{' '}
            <span className="font-bold">{submittedData && submittedData.phone}</span>
          </p>

          <p className="text-lg">
            Didn't receive OTP{' '}
            <span className="font-bold cursor-pointer text-blue-500" onClick={(e) => handleResendOTP(e)}>
              Resend OTP
            </span>
          </p>

          <div className="my-5">
            <OtpInput
              inputStyle={{
                width: '2.5rem',
                height: '3rem',
                margin: '0 1rem',
                fontSize: '1.5rem',
                borderRadius: 'var(--radius-4)',
                border: '1px solid var(--accent-7)',
              }}
              value={otp}
              onChange={setOtp}
              numInputs={4}
              renderSeparator={<span>-</span>}
              renderInput={(props) => <input {...props} />}
            />
          </div>

          <Button
            disabled={OTPLoading || otp.length < 4}
            onClick={(e) => handleVerification(e)}
            className="cursor-pointer w-full"
          >
            {OTPLoading ? (
              <Flex gap="2" align="center">
                <Spinner size="1" />
                Loading...
              </Flex>
            ) : (
              'Verify'
            )}
          </Button>
        </Flex>
      )}
    </form>
  );
};

export default RegisterForm;